import React, { useMemo, useState } from "react";
import CustomTable from "components/Table/CustomTable";
import { useModal } from "contexts/ModalProvider";
import toast from "react-hot-toast";
import { Dropdown, DropdownMenu, DropdownToggle, DropdownItem, Input, FormGroup } from "reactstrap";
import edit from "assets/icons/edit.svg";
import trash from "assets/icons/trash.svg";
import check from "assets/icons/check-circle.svg";
import slash from "assets/icons/slash-circle.svg";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { CampaignsSchema } from "model/campaigns/campaigns.schema";
import ToastNotification from "components/Toast/ToastNotification";
import { useUpdateCampaignMutation, useDeleteCampaignMutation } from "services/campaigns/api";

// Custom Hooks
import useTableChangeHandler from "hooks/useTableChangeHandler";

const CampaignsTable = ({ data, onStatusChange, onTableChange, error, isLoading }) => {
  const { modal } = useModal();
  const [actionsDropdown, setActionsDropdown] = useState({});

  const [updateCampaign] = useUpdateCampaignMutation();
  const [deleteCampaign] = useDeleteCampaignMutation();

  const { handleSort, handleFilter, handleSearch, handlePageChange } = useTableChangeHandler(onTableChange);

  const columns = useMemo(
    () => [
      {
        header: "แคมเปญ",
        accessor: "name",
        sortable: true,
      },
      {
        header: "ระยะเวลาแคมเปญ",
        accessor: "dateRange",
        render: (item) => (
          <div>
            <div className="text-nowrap">วันที่เริ่มต้น : {dayjs(item.startDate).format("YYYY-MM-DD")}</div>
            <div className="text-nowrap">วันที่สิ้นสุด : {dayjs(item.endDate).format("YYYY-MM-DD")}</div>
          </div>
        ),
      },
      {
        header: "โบนัสที่ได้",
        accessor: "bonus",
        sortable: true,
        render: (item) => (item.bonusType === "Percent" ? `${item.bonusRate}%` : `฿${item.bonusAmount}`),
      },
      {
        header: "จำนวนโควต้าทั้งหมด",
        accessor: "quota",
        sortable: true,
        render: (item) => (item.quotaType === "Unlimited" ? "ไม่จำกัด" : item.quotaLimit),
      },
      {
        header: "ใช้โบนัสไปแล้ว",
        accessor: "quotaUsed",
        sortable: true,
        render: (item) => item.quotaUsed ?? 0,
      },
      {
        header: "สถานะ",
        accessor: "status",
        align: "center",
        render: (item) => (
          <>
            <div className="form-switch">
              <Input
                type="switch"
                role="switch"
                defaultChecked={item.status == "Active"}
                onChange={(e) => {
                  onStatusChange(item.id, e.target.checked);
                  updateCampaign({
                    id: item.id,
                    status: e.target.checked ? "Active" : "Inactive",
                  });
                }}
              />
            </div>
          </>
        ),
      },
      {
        header: "",
        accessor: "actions",
        width: 50,
        render: (item) => (
          <Dropdown
            size="thin"
            isOpen={actionsDropdown[item.id]}
            toggle={() =>
              setActionsDropdown({
                ...actionsDropdown,
                [item.id]: !actionsDropdown[item.id],
              })
            }
          >
            <DropdownToggle tag="span" className="cursor-pointer">
              <i className="mdi mdi-dots-vertical" />
            </DropdownToggle>
            <DropdownMenu>
              <Link to={`/campaigns/form/${item.id}`} className="text-primary">
                <DropdownItem>
                  <img src={edit} alt="แก้ไข" />
                  แก้ไขข้อมูล
                </DropdownItem>
              </Link>
              <DropdownItem onClick={() => handleDeleteCampaigns(item)}>
                <img src={trash} />
                ลบแคมเปญ
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        ),
      },
    ],
    [actionsDropdown, setActionsDropdown, data]
  );

  const handleDeleteCampaigns = async (item) => {
    const result = await modal.alert.danger({
      title: "คุณยืนยันที่จะลบแคมเปญนี้หรือไม่?",
      message: `ตรวจสอบรายละเอียดแคมเปญที่คุณต้องการลบออกจากระบบว่าถูกต้องหรือไม่? เมื่อลบแล้ว รายการแคมเปญที่ถูกลบจะไม่สามารถเรียกกลับมาได้`,
      confirmText: "ลบแคมเปญ",
      cancelText: "ยกเลิก",
      children: (
        <div className="d-flex flex-column gap-2">
          <div className="text-xl fw-semibold">{item.name}</div>
          <div className="text-xl">
            โบนัสที่ได้: <span className="fw-semibold">{item.bonusRate}%</span>
          </div>
          <div className="text-xl">
            จำนวนโควต้าทั้งหมด: <span className="fw-semibold">{item.quotaType === "Unlimited" ? "ไม่จำกัด" : item.quotaLimit}</span>
          </div>
          <div className="text-xl">
            ใช้โบนัสไปแล้ว: <span className="fw-semibold">{item.quotaUsed}</span>
          </div>
        </div>
      ),
    });
    if (result) {
      try {
        await deleteCampaign(item.id);
        let toastId = toast.custom(<ToastNotification variant="success" icon={check} title={`ลบแคมเปญออกจากระบบเรียบร้อยแล้ว`} description={`แคมเปญ "${item.name}" ถูกลบออกจากระบบเรียบร้อยแล้ว`} onSecondaryClick={() => toast.dismiss(toastId)} />, { position: "top-right", duration: 5000 });
        // Refresh data
        if (onTableChange) {
          onTableChange({});
        }
      } catch (error) {
        console.error("Failed to delete campaign:", error);
        let toastId = toast.custom(<ToastNotification variant="danger" icon={slash} title="เกิดข้อผิดพลาด" description="ไม่สามารถลบแคมเปญได้ กรุณาลองใหม่อีกครั้ง" onSecondaryClick={() => toast.dismiss(toastId)} />, { position: "top-right", duration: 5000 });
      }
    }
  };

  return (
    <div data-testid="campaigns-table">
      <div className="text-lg mb-4">รายการแคมเปญที่มีในระบบทั้งหมด * {data?.pagination?.total?.toLocaleString() || 0} รายการ *</div>

      <CustomTable keyField="id" data={data?.data || []} columns={columns} className="campaigns-table" itemsPerPage={data?.pagination?.limit || 10} totalItems={data?.pagination?.total || 0} onSort={handleSort} onPageChange={handlePageChange} onFilter={handleFilter} onSearch={handleSearch} status={isLoading ? "loading" : error ? "error" : data?.data?.length === 0 ? "empty" : "normal"} errorMessage={error?.message || "เกิดข้อผิดพลาดในการโหลดข้อมูล"} emptyMessage="ไม่พบข้อมูลแคมเปญ" />
    </div>
  );
};

const withYupValidation = (WrappedComponent, schema) => {
  return function WithYupValidation(props) {
    try {
      if (!props.isLoading && !props.error && props.data) {
        schema.validateSync(props.data);
      }
      return <WrappedComponent {...props} />;
    } catch (error) {
      return (
        <div className="alert alert-danger">
          <strong>Validation Error:</strong> {error.message}
        </div>
      );
    }
  };
};

const ValidatedTable = withYupValidation(CampaignsTable, CampaignsSchema);

export default ValidatedTable;
